import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import axios from 'axios';
import './AdminDashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3006/api';

function EnrollmentSync() {
    const [teacherTypes, setTeacherTypes] = useState([]);

    // Registration dialog
    const [showRegisterDialog, setShowRegisterDialog] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [registering, setRegistering] = useState(false);
    const [nicLookupLoading, setNicLookupLoading] = useState(false);
    const [nicLookupFound, setNicLookupFound] = useState(null);
    const [formData, setFormData] = useState({
        signingId: '',
        firstName: '',
        lastName: '',
        teacherTypeId: null,
        nic: ''
    });

    const toast = useRef(null);

    const [reactivating, setReactivating] = useState(null);

    const [mappings, setMappings] = useState([]);
    const [mappingsLoading, setMappingsLoading] = useState(false);
    const [mappingsFilter, setMappingsFilter] = useState('');
    const [mappingsSigningIdFilter, setMappingsSigningIdFilter] = useState('');
    const [mappingsFirst, setMappingsFirst] = useState(0);
    const [mappingsRows, setMappingsRows] = useState(15);
    const [mappingsTotalRecords, setMappingsTotalRecords] = useState(0);
    const [mappingsSortField, setMappingsSortField] = useState('signingId');
    const [mappingsSortOrder, setMappingsSortOrder] = useState(1);
    const [mappingsStatus, setMappingsStatus] = useState('active');
    const mappingsSearchTimeout = useRef(null);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [editFormData, setEditFormData] = useState({
        firstName: '', lastName: '', signingId: '', teacherTypeId: null, nic: ''
    });
    const [saving, setSaving] = useState(false);
    const [unlinking, setUnlinking] = useState(null);

    const nicLookupTimeout = useRef(null);

    const getAuthHeaders = () => ({
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
    });

    const showToast = (severity, summary, detail) => {
        toast.current?.show({ severity, summary, detail, life: 4000 });
    };

    const fetchTeacherTypes = useCallback(async () => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/enrollment-sync/teacher-types`,
                getAuthHeaders()
            );
            if (response.data.success) {
                setTeacherTypes(response.data.data.map(t => ({
                    label: t.name,
                    value: t.id
                })));
            }
        } catch (error) {
            console.error('Error fetching teacher types:', error);
        }
    }, []);

    const lookupNicForEdit = useCallback(async (nic) => {
        const cleaned = (nic || '').trim().toUpperCase();
        if (cleaned.length < 5) {
            return;
        }
        setNicLookupLoading(true);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/enrollment-sync/lookup-nic/${encodeURIComponent(cleaned)}`,
                getAuthHeaders()
            );
            if (response.data?.success && response.data?.exists) {
                const d = response.data.data;
                setEditFormData(prev => ({
                    ...prev,
                    nic: d.nic || prev.nic,
                    firstName: d.firstName || prev.firstName,
                    lastName: d.lastName || prev.lastName,
                    teacherTypeId: d.teacherTypeId || prev.teacherTypeId,
                    signingId: d.signingId != null ? String(d.signingId) : prev.signingId
                }));
            }
        } catch (error) {
            console.error('NIC lookup (edit) error:', error);
        } finally {
            setNicLookupLoading(false);
        }
    }, []);

    const fetchMappings = useCallback(async (page = 0, rows = 15, search = '', signingId = '', sortField = 'addedDate', sortOrder = -1, status = 'all') => {
        setMappingsLoading(true);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/enrollment-sync/mappings?page=${page}&rows=${rows}&search=${encodeURIComponent(search)}&signingId=${encodeURIComponent(signingId)}&sortField=${sortField}&sortOrder=${sortOrder}&status=${status}`,
                getAuthHeaders()
            );
            if (response.data.success) {
                setMappings(response.data.data);
                setMappingsTotalRecords(response.data.totalRecords || 0);
            }
        } catch (error) {
            console.error('Error fetching mappings:', error);
            showToast('error', 'Error', 'Failed to fetch mappings');
        } finally {
            setMappingsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMappings(0, mappingsRows, mappingsFilter, mappingsSigningIdFilter, mappingsSortField, mappingsSortOrder, mappingsStatus);
        fetchTeacherTypes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onMappingsPage = (e) => {
        setMappingsFirst(e.first);
        setMappingsRows(e.rows);
        fetchMappings(Math.floor(e.first / e.rows), e.rows, mappingsFilter, mappingsSigningIdFilter, mappingsSortField, mappingsSortOrder, mappingsStatus);
    };

    const onMappingsSort = (e) => {
        setMappingsSortField(e.sortField);
        setMappingsSortOrder(e.sortOrder);
        setMappingsFirst(0);
        fetchMappings(0, mappingsRows, mappingsFilter, mappingsSigningIdFilter, e.sortField, e.sortOrder, mappingsStatus);
    };

    const onMappingsStatusChange = (value) => {
        setMappingsStatus(value);
        setMappingsFirst(0);
        fetchMappings(0, mappingsRows, mappingsFilter, mappingsSigningIdFilter, mappingsSortField, mappingsSortOrder, value);
    };

    const onMappingsSearch = (value) => {
        setMappingsFilter(value);
        setMappingsFirst(0);
        if (mappingsSearchTimeout.current) clearTimeout(mappingsSearchTimeout.current);
        mappingsSearchTimeout.current = setTimeout(() => {
            fetchMappings(0, mappingsRows, value, mappingsSigningIdFilter, mappingsSortField, mappingsSortOrder, mappingsStatus);
        }, 400);
    };

    const onMappingsSigningIdSearch = (value) => {
        setMappingsSigningIdFilter(value);
        setMappingsFirst(0);
        if (mappingsSearchTimeout.current) clearTimeout(mappingsSearchTimeout.current);
        mappingsSearchTimeout.current = setTimeout(() => {
            fetchMappings(0, mappingsRows, mappingsFilter, value, mappingsSortField, mappingsSortOrder, mappingsStatus);
        }, 400);
    };

    const openRegisterDialog = (record) => {
        setSelectedRecord(record);
        setNicLookupFound(null);
        // Pre-fill form with existing data if available
        setFormData({
            signingId: record.existingTid ? String(record.existingTid) : '',
            firstName: record.existingFirstName || '',
            lastName: record.existingLastName || '',
            teacherTypeId: record.existingTeacherTypeId || null,
            nic: record.existingNic || ''
        });
        setShowRegisterDialog(true);
    };

    const lookupNic = useCallback(async (nic) => {
        const cleaned = (nic || '').trim().toUpperCase();
        if (cleaned.length < 5) {
            setNicLookupFound(null);
            return;
        }
        setNicLookupLoading(true);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/enrollment-sync/lookup-nic/${encodeURIComponent(cleaned)}`,
                getAuthHeaders()
            );
            if (response.data?.success && response.data?.exists) {
                const d = response.data.data;
                setNicLookupFound(d);
                setFormData(prev => ({
                    ...prev,
                    nic: d.nic || prev.nic,
                    firstName: d.firstName || prev.firstName,
                    lastName: d.lastName || prev.lastName,
                    teacherTypeId: d.teacherTypeId || prev.teacherTypeId,
                    signingId: d.signingId != null ? String(d.signingId) : prev.signingId
                }));
            } else {
                setNicLookupFound(null);
            }
        } catch (error) {
            console.error('NIC lookup error:', error);
            setNicLookupFound(null);
        } finally {
            setNicLookupLoading(false);
        }
    }, []);

    const handleRegister = async () => {
        if (!formData.firstName || !formData.lastName || !formData.teacherTypeId) {
            showToast('warn', 'Validation', 'First Name, Last Name and Teacher Type are required');
            return;
        }

        setRegistering(true);
        try {
            const isPartial = selectedRecord.gapType !== 'NO_ENROLLMENT';
            const endpoint = isPartial ? 'complete-chain' : 'register';

            const body = {
                empId: selectedRecord.empId,
                signingId: formData.signingId ? parseInt(formData.signingId) : null,
                firstName: formData.firstName,
                lastName: formData.lastName,
                teacherTypeId: formData.teacherTypeId,
                regionId: selectedRecord.regionId,
                nic: formData.nic || null
            };

            if (isPartial) {
                body.fruId = selectedRecord.fruId || null;
                body.gupId = selectedRecord.gupId || null;
            }

            const response = await axios.post(
                `${API_BASE_URL}/enrollment-sync/${endpoint}`,
                body,
                getAuthHeaders()
            );

            if (response.data.success) {
                showToast('success', 'Registered',
                    `${formData.firstName} ${formData.lastName} linked to emp_id ${selectedRecord.empId}`);
                setShowRegisterDialog(false);
                fetchMappings(Math.floor(mappingsFirst / mappingsRows), mappingsRows, mappingsFilter, mappingsSortField, mappingsSortOrder, mappingsStatus);
            }
        } catch (error) {
            const msg = error.response?.data?.message || error.message;
            showToast('error', 'Registration Failed', msg);
        } finally {
            setRegistering(false);
        }
    };

    const handleReactivate = async (record) => {
        if (!record.teacherId) {
            showToast('error', 'Error', 'No teacher ID found for reactivation');
            return;
        }
        setReactivating(record.empId);
        try {
            const response = await axios.post(
                `${API_BASE_URL}/enrollment-sync/reactivate`,
                { teacherId: record.teacherId },
                getAuthHeaders()
            );
            if (response.data.success) {
                showToast('success', 'Reactivated', response.data.message);
                fetchMappings(Math.floor(mappingsFirst / mappingsRows), mappingsRows, mappingsFilter, mappingsSortField, mappingsSortOrder, mappingsStatus);
            }
        } catch (error) {
            const msg = error.response?.data?.message || error.message;
            showToast('error', 'Reactivation Failed', msg);
        } finally {
            setReactivating(null);
        }
    };

    // Review Mappings handlers
    const openEditDialog = (record) => {
        setEditRecord(record);
        setEditFormData({
            firstName: record.firstName || '',
            lastName: record.lastName || '',
            signingId: record.signingId != null ? String(record.signingId) : '',
            teacherTypeId: record.teacherTypeId || null,
            nic: record.nic || ''
        });
        setShowEditDialog(true);
    };

    const handleSaveEdit = async () => {
        if (!editFormData.firstName || !editFormData.lastName) {
            showToast('warn', 'Validation', 'First Name and Last Name are required');
            return;
        }
        setSaving(true);
        try {
            const response = await axios.put(
                `${API_BASE_URL}/enrollment-sync/mappings/${editRecord.fruId}`,
                {
                    firstName: editFormData.firstName,
                    lastName: editFormData.lastName,
                    signingId: editFormData.signingId ? parseInt(editFormData.signingId) : null,
                    teacherTypeId: editFormData.teacherTypeId,
                    nic: editFormData.nic || null
                },
                getAuthHeaders()
            );
            if (response.data.success) {
                showToast('success', 'Updated', response.data.message);
                setShowEditDialog(false);
                fetchMappings(Math.floor(mappingsFirst / mappingsRows), mappingsRows, mappingsFilter, mappingsSortField, mappingsSortOrder, mappingsStatus);
            }
        } catch (error) {
            const msg = error.response?.data?.message || error.message;
            showToast('error', 'Update Failed', msg);
        } finally {
            setSaving(false);
        }
    };

    const handleUnlink = (record) => {
        confirmDialog({
            message: (
                <div>
                    <p>This will <strong>disconnect</strong> emp_id <strong>{record.empId}</strong> from
                    teacher "<strong>{record.firstName} {record.lastName}</strong>".</p>
                    <p style={{ marginTop: '10px', color: '#e74c3c' }}>
                        The emp_id will reappear in the Unmatched tab for re-mapping.
                    </p>
                </div>
            ),
            header: 'Confirm Unlink',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            acceptLabel: 'Unlink',
            rejectLabel: 'Cancel',
            accept: async () => {
                setUnlinking(record.fruId);
                try {
                    const response = await axios.post(
                        `${API_BASE_URL}/enrollment-sync/unlink`,
                        { fruId: record.fruId },
                        getAuthHeaders()
                    );
                    if (response.data.success) {
                        showToast('success', 'Unlinked', response.data.message);
                        fetchMappings(Math.floor(mappingsFirst / mappingsRows), mappingsRows, mappingsFilter, mappingsSortField, mappingsSortOrder, mappingsStatus);
                    }
                } catch (error) {
                    const msg = error.response?.data?.message || error.message;
                    showToast('error', 'Unlink Failed', msg);
                } finally {
                    setUnlinking(null);
                }
            }
        });
    };

    // Column templates
    const empIdTemplate = (rowData) => (
        <span style={{ fontWeight: '700', fontSize: '1.1rem', color: '#1e3a5f' }}>
            {rowData.empId}
        </span>
    );

    const punchCountTemplate = (rowData) => (
        <div style={{ textAlign: 'center' }}>
            <span style={{ 
                fontWeight: '600', 
                color: rowData.punchCount > 100 ? '#e74c3c' : rowData.punchCount > 30 ? '#f39c12' : '#27ae60',
                fontSize: '1rem'
            }}>
                {rowData.punchCount}
            </span>
        </div>
    );

    // Review Mappings column templates
    const mappingNameTemplate = (rowData) => (
        <span style={{ fontWeight: '600' }}>
            {rowData.firstName} {rowData.lastName}
        </span>
    );

    const mappingSigningIdTemplate = (rowData) => {
        if (rowData.signingId != null) {
            return <span>{rowData.signingId}</span>;
        }
        return <span style={{ color: '#ccc', fontStyle: 'italic' }}>Not set</span>;
    };

    const mappingTeacherTypeTemplate = (rowData) => {
        if (rowData.teacherTypeName) {
            return <Tag value={rowData.teacherTypeName} severity="info" />;
        }
        return <span style={{ color: '#ccc', fontStyle: 'italic' }}>None</span>;
    };

    const handleToggleStatus = (record) => {
        if (!record.teacherId) return;
        const newStatus = record.teacherActive === 1 ? 'Inactive' : 'Active';
        const name = `${record.firstName} ${record.lastName}`.trim();
        confirmDialog({
            message: `Set "${name}" to ${newStatus}?`,
            header: `Confirm ${newStatus}`,
            icon: record.teacherActive === 1 ? 'pi pi-ban' : 'pi pi-check-circle',
            acceptClassName: record.teacherActive === 1 ? 'p-button-warning' : 'p-button-success',
            accept: async () => {
                try {
                    const response = await axios.post(
                        `${API_BASE_URL}/enrollment-sync/toggle-status`,
                        { teacherId: record.teacherId },
                        getAuthHeaders()
                    );
                    if (response.data.success) {
                        showToast('success', 'Status Changed', response.data.message);
                        fetchMappings(Math.floor(mappingsFirst / mappingsRows), mappingsRows, mappingsFilter, mappingsSortField, mappingsSortOrder, mappingsStatus);
                    }
                } catch (error) {
                    const msg = error.response?.data?.message || error.message;
                    showToast('error', 'Toggle Failed', msg);
                }
            }
        });
    };

    const mappingStatusTemplate = (rowData) => {
        if (!rowData.teacherId) {
            return <Button label="No Teacher" className="p-button-sm p-button-danger" disabled />;
        }
        return rowData.teacherActive === 1
            ? (
                <Button
                    label="Active"
                    icon="pi pi-check"
                    className="p-button-sm p-button-success"
                    onClick={() => handleToggleStatus(rowData)}
                />
            )
            : (
                <Button
                    label="Inactive"
                    icon="pi pi-ban"
                    className="p-button-sm p-button-danger"
                    onClick={() => handleToggleStatus(rowData)}
                />
            );
    };

    const mappingAddedDateTemplate = (rowData) => {
        if (!rowData.addedDate) return '-';
        const d = new Date(rowData.addedDate);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const lastPunchDateTemplate = (rowData) => {
        if (!rowData.lastPunchDate) return '-';
        const d = new Date(rowData.lastPunchDate);
        if (Number.isNaN(d.getTime())) return '-';
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const mappingActionTemplate = (rowData) => (
        <div style={{ display: 'flex', gap: '5px' }}>
            <Button
                icon="pi pi-pencil"
                className="p-button-sm p-button-outlined p-button-info"
                onClick={() => openEditDialog(rowData)}
                tooltip="Edit mapping"
                tooltipOptions={{ position: 'top' }}
            />
            <Button
                icon={unlinking === rowData.fruId ? 'pi pi-spin pi-spinner' : 'pi pi-link'}
                className="p-button-sm p-button-outlined p-button-danger"
                onClick={() => handleUnlink(rowData)}
                disabled={unlinking === rowData.fruId}
                tooltip="Unlink (disconnect emp_id from teacher)"
                tooltipOptions={{ position: 'top' }}
            />
        </div>
    );

    const mappingsTableHeader = (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i className="pi pi-list" style={{ fontSize: '1.2rem', color: '#1e3a5f' }}></i>
                <span style={{ fontWeight: '600', fontSize: '1.1rem', color: '#1e3a5f' }}>
                    All Enrollment Mappings ({mappingsTotalRecords})
                </span>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <Dropdown
                    value={mappingsStatus}
                    options={[
                        { label: 'All Status', value: 'all' },
                        { label: 'Active', value: 'active' },
                        { label: 'Inactive / No Teacher', value: 'inactive' }
                    ]}
                    onChange={(e) => onMappingsStatusChange(e.value)}
                    style={{ width: '180px' }}
                    className="p-inputtext-sm"
                />
                <span className="p-input-icon-left">
                    <i className="pi pi-id-card" />
                    <InputText
                        value={mappingsSigningIdFilter}
                        onChange={(e) => onMappingsSigningIdSearch(e.target.value)}
                        placeholder="Signing ID..."
                        style={{ width: '170px' }}
                    />
                </span>
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText
                        value={mappingsFilter}
                        onChange={(e) => onMappingsSearch(e.target.value)}
                        placeholder="Search by name, emp_id, NIC..."
                        style={{ width: '250px' }}
                    />
                </span>
                <Button
                    icon="pi pi-refresh"
                    className="p-button-outlined p-button-sm"
                    onClick={() => { setMappingsFirst(0); fetchMappings(0, mappingsRows, mappingsFilter, mappingsSigningIdFilter, mappingsSortField, mappingsSortOrder, mappingsStatus); }}
                    tooltip="Refresh"
                />
            </div>
        </div>
    );

    return (
        <div style={{ padding: '0' }}>
            <Toast ref={toast} />
            <ConfirmDialog />

            <div style={{ background: '#fff', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                            <DataTable
                                value={mappings}
                                header={mappingsTableHeader}
                                lazy
                                paginator
                                first={mappingsFirst}
                                rows={mappingsRows}
                                totalRecords={mappingsTotalRecords}
                                onPage={onMappingsPage}
                                sortField={mappingsSortField}
                                sortOrder={mappingsSortOrder}
                                onSort={onMappingsSort}
                                rowsPerPageOptions={[10, 15, 25, 50]}
                                loading={mappingsLoading}
                                emptyMessage="No enrollment mappings found."
                                stripedRows
                                size="small"
                                scrollable
                                scrollHeight="calc(100vh - 310px)"
                                style={{ fontSize: '0.9rem' }}
                            >
                                <Column header="#" body={(rowData, options) => mappingsFirst + options.rowIndex + 1} frozen style={{ width: '50px', textAlign: 'center', backgroundColor: '#fff' }} />
                                <Column field="empId" header="emp_id" body={empIdTemplate} sortable style={{ width: '80px' }} />
                                <Column field="signingId" header="Signing ID" body={mappingSigningIdTemplate} sortable style={{ width: '90px' }} />
                                <Column field="firstName" header="Teacher Name" body={mappingNameTemplate} sortable style={{ width: '180px' }} />
                                <Column field="teacherTypeName" header="Type" body={mappingTeacherTypeTemplate} sortable style={{ width: '120px' }} />
                                <Column field="nic" header="NIC" sortable style={{ width: '130px' }} />
                                <Column field="regionName" header="Region" sortable style={{ width: '140px' }} />
                                <Column field="punchCount" header="Punches" body={punchCountTemplate} sortable style={{ width: '80px', textAlign: 'center' }} />
                                <Column field="addedDate" header="Mapped On" body={mappingAddedDateTemplate} sortable style={{ width: '110px' }} />
                                <Column field="lastPunchDate" header="Last Punch Date" body={lastPunchDateTemplate} sortable style={{ width: '130px' }} />
                                <Column header="Status" body={mappingStatusTemplate} style={{ width: '100px' }} />
                                <Column body={mappingActionTemplate} style={{ width: '100px', textAlign: 'center' }} />
                            </DataTable>
            </div>

            {/* Registration Dialog */}
            <Dialog
                header={
                    <div>
                        <i className="pi pi-user-plus" style={{ marginRight: '8px' }}></i>
                        Register Teacher for emp_id: {selectedRecord?.empId}
                    </div>
                }
                visible={showRegisterDialog}
                style={{ width: '520px' }}
                onHide={() => setShowRegisterDialog(false)}
                modal
            >
                {selectedRecord && (
                    <div>
                        {/* Context info */}
                        <div style={{ 
                            background: '#f8f9fa', padding: '12px 15px', borderRadius: '8px', 
                            marginBottom: '20px', fontSize: '0.9rem' 
                        }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                <div><strong>emp_id:</strong> {selectedRecord.empId}</div>
                                <div><strong>Machine:</strong> {selectedRecord.regionName}</div>
                                <div><strong>Punches:</strong> {selectedRecord.punchCount}</div>
                                <div><strong>Last Seen:</strong> {selectedRecord.lastSeen ? new Date(selectedRecord.lastSeen).toLocaleDateString() : '-'}</div>
                                <div><strong>Issue:</strong> {selectedRecord.gapType?.replace('_', ' ')}</div>
                            </div>
                        </div>

                        {/* Pre-fill notice when data exists */}
                        {selectedRecord.existingName && (
                            <Message
                                severity="info"
                                style={{ marginBottom: '15px', width: '100%' }}
                                content={
                                    <div style={{ fontSize: '0.85rem', lineHeight: '1.5' }}>
                                        <strong>Teacher found in system:</strong> {selectedRecord.existingName}. 
                                        Fields have been pre-filled. Only the missing enrollment link will be created.
                                    </div>
                                }
                            />
                        )}

                        {/* Warning about emp_id vs signing ID */}
                        <Message
                            severity="warn"
                            style={{ marginBottom: '20px', width: '100%' }}
                            content={
                                <div style={{ fontSize: '0.85rem', lineHeight: '1.5' }}>
                                    <strong>Important:</strong> The <strong>emp_id ({selectedRecord.empId})</strong> is the 
                                    machine's internal enrollment number. The <strong>Signing ID</strong> is what the teacher 
                                    sees on the machine display when they punch. These are often different numbers. 
                                    Please verify both on the physical machine before submitting.
                                </div>
                            }
                        />

                        {/* Form */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            {/* NIC */}
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '0.9rem' }}>
                                    NIC <span style={{ color: '#999', fontWeight: '400' }}>— optional, can add later</span>
                                </label>
                                <InputText
                                    value={formData.nic}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        const normalized = v.trim().toUpperCase();
                                        setFormData(prev => ({ ...prev, nic: v }));
                                        setNicLookupFound(null);
                                        if (nicLookupTimeout.current) clearTimeout(nicLookupTimeout.current);
                                        nicLookupTimeout.current = setTimeout(() => lookupNic(normalized), 400);
                                    }}
                                    placeholder="Enter NIC (optional)"
                                    style={{ width: '100%' }}
                                    autoFocus
                                />
                                {nicLookupLoading && (
                                    <div style={{ fontSize: '0.8rem', color: '#777', marginTop: '4px' }}>Checking NIC...</div>
                                )}
                                {!nicLookupLoading && nicLookupFound && (
                                    <div style={{ fontSize: '0.8rem', color: '#2e7d32', marginTop: '4px' }}>
                                        NIC found. Details loaded.
                                    </div>
                                )}
                            </div>

                            {/* emp_id (read-only) */}
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '0.9rem' }}>
                                    emp_id (Enrollment Number) <span style={{ color: '#999', fontWeight: '400' }}>— read only</span>
                                </label>
                                <InputText
                                    value={selectedRecord.empId}
                                    disabled
                                    style={{ width: '100%', background: '#f0f0f0' }}
                                />
                            </div>

                            {/* Signing ID */}
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '0.9rem' }}>
                                    Signing ID <span style={{ color: '#999', fontWeight: '400' }}>— from machine display</span>
                                </label>
                                <InputText
                                    value={formData.signingId}
                                    onChange={(e) => setFormData({ ...formData, signingId: e.target.value })}
                                    placeholder="Enter the Signing ID shown on machine"
                                    style={{ width: '100%' }}
                                    keyfilter="int"
                                />
                            </div>

                            {/* First Name */}
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '0.9rem' }}>
                                    First Name <span style={{ color: '#e74c3c' }}>*</span>
                                </label>
                                <InputText
                                    value={formData.firstName}
                                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                    placeholder="Enter teacher's first name"
                                    style={{ width: '100%' }}
                                    autoFocus
                                />
                            </div>

                            {/* Last Name */}
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '0.9rem' }}>
                                    Last Name <span style={{ color: '#e74c3c' }}>*</span>
                                </label>
                                <InputText
                                    value={formData.lastName}
                                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                    placeholder="Enter teacher's last name"
                                    style={{ width: '100%' }}
                                />
                            </div>

                            {/* Teacher Type */}
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '0.9rem' }}>
                                    Teacher Type <span style={{ color: '#e74c3c' }}>*</span>
                                </label>
                                <Dropdown
                                    value={formData.teacherTypeId}
                                    options={teacherTypes}
                                    onChange={(e) => setFormData({ ...formData, teacherTypeId: e.value })}
                                    placeholder="Select teacher type"
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </div>

                        {/* What will be created */}
                        <div style={{ 
                            background: '#e8f5e9', padding: '12px 15px', borderRadius: '8px', 
                            marginTop: '20px', fontSize: '0.85rem', color: '#2e7d32' 
                        }}>
                            <strong>On submit, this will create:</strong>
                            <ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
                                {selectedRecord.gapType === 'NO_ENROLLMENT' && (
                                    <>
                                        <li>User profile (name you entered above)</li>
                                        <li>Teacher record (tid = Signing ID)</li>
                                        <li>Enrollment link (enrollement_id = {selectedRecord.empId})</li>
                                    </>
                                )}
                                {selectedRecord.gapType === 'NO_PROFILE' && (
                                    <>
                                        <li>User profile (name you entered above)</li>
                                        <li>Teacher record (tid = Signing ID)</li>
                                    </>
                                )}
                                {selectedRecord.gapType === 'NO_TEACHER' && (
                                    <li>Teacher record (tid = Signing ID)</li>
                                )}
                            </ul>
                            <div style={{ marginTop: '8px' }}>
                                All future punches for <strong>emp_id {selectedRecord.empId}</strong> will appear in reports immediately.
                            </div>
                        </div>

                        {/* Submit button */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                            <Button
                                label="Cancel"
                                icon="pi pi-times"
                                className="p-button-text"
                                onClick={() => setShowRegisterDialog(false)}
                            />
                            <Button
                                label={registering ? 'Registering...' : 'Register & Link'}
                                icon={registering ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                                onClick={handleRegister}
                                disabled={registering || !formData.firstName || !formData.lastName || !formData.teacherTypeId}
                                style={{ background: '#27ae60', border: 'none' }}
                            />
                        </div>
                    </div>
                )}
            </Dialog>

            {/* Edit Mapping Dialog */}
            <Dialog
                header={
                    <div>
                        <i className="pi pi-pencil" style={{ marginRight: '8px' }}></i>
                        Edit Mapping for emp_id: {editRecord?.empId}
                    </div>
                }
                visible={showEditDialog}
                style={{ width: '520px' }}
                onHide={() => setShowEditDialog(false)}
                modal
            >
                {editRecord && (
                    <div>
                        {/* Context info */}
                        <div style={{ 
                            background: '#f8f9fa', padding: '12px 15px', borderRadius: '8px', 
                            marginBottom: '20px', fontSize: '0.9rem' 
                        }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                <div><strong>emp_id:</strong> {editRecord.empId}</div>
                                <div><strong>Region:</strong> {editRecord.regionName}</div>
                                <div><strong>Mapped On:</strong> {editRecord.addedDate ? new Date(editRecord.addedDate).toLocaleDateString() : '-'}</div>
                                <div><strong>Last Punch Date:</strong> {editRecord.lastPunchDate ? new Date(editRecord.lastPunchDate).toLocaleDateString() : '-'}</div>
                                <div><strong>Punches:</strong> {editRecord.punchCount}</div>
                            </div>
                        </div>

                        <Message
                            severity="info"
                            style={{ marginBottom: '20px', width: '100%' }}
                            content={
                                <div style={{ fontSize: '0.85rem', lineHeight: '1.5' }}>
                                    Edit the fields below to correct a wrong mapping. Changes are saved to the 
                                    database immediately. Use <strong>Unlink</strong> if you need to disconnect 
                                    this emp_id entirely and re-map it to a different teacher.
                                </div>
                            }
                        />

                        {/* Edit Form */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            {/* NIC */}
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '0.9rem' }}>
                                    NIC
                                </label>
                                <InputText
                                    value={editFormData.nic}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        const normalized = v.trim().toUpperCase();
                                        setEditFormData(prev => ({ ...prev, nic: v }));
                                        if (nicLookupTimeout.current) clearTimeout(nicLookupTimeout.current);
                                        nicLookupTimeout.current = setTimeout(() => lookupNicForEdit(normalized), 400);
                                    }}
                                    placeholder="Enter NIC"
                                    style={{ width: '100%' }}
                                    autoFocus
                                />
                                {nicLookupLoading && (
                                    <div style={{ fontSize: '0.8rem', color: '#777', marginTop: '4px' }}>Checking NIC...</div>
                                )}
                            </div>

                            {/* emp_id (read-only) */}
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '0.9rem' }}>
                                    emp_id (Enrollment Number) <span style={{ color: '#999', fontWeight: '400' }}>— read only</span>
                                </label>
                                <InputText
                                    value={editRecord.empId}
                                    disabled
                                    style={{ width: '100%', background: '#f0f0f0' }}
                                />
                            </div>

                            {/* Signing ID */}
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '0.9rem' }}>
                                    Signing ID <span style={{ color: '#999', fontWeight: '400' }}>— from machine display</span>
                                </label>
                                <InputText
                                    value={editFormData.signingId}
                                    onChange={(e) => setEditFormData({ ...editFormData, signingId: e.target.value })}
                                    placeholder="Enter the Signing ID shown on machine"
                                    style={{ width: '100%' }}
                                    keyfilter="int"
                                />
                            </div>

                            {/* First Name */}
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '0.9rem' }}>
                                    First Name <span style={{ color: '#e74c3c' }}>*</span>
                                </label>
                                <InputText
                                    value={editFormData.firstName}
                                    onChange={(e) => setEditFormData({ ...editFormData, firstName: e.target.value })}
                                    placeholder="Enter teacher's first name"
                                    style={{ width: '100%' }}
                                />
                            </div>

                            {/* Last Name */}
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '0.9rem' }}>
                                    Last Name <span style={{ color: '#e74c3c' }}>*</span>
                                </label>
                                <InputText
                                    value={editFormData.lastName}
                                    onChange={(e) => setEditFormData({ ...editFormData, lastName: e.target.value })}
                                    placeholder="Enter teacher's last name"
                                    style={{ width: '100%' }}
                                />
                            </div>

                            {/* Teacher Type */}
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '0.9rem' }}>
                                    Teacher Type
                                </label>
                                <Dropdown
                                    value={editFormData.teacherTypeId}
                                    options={teacherTypes}
                                    onChange={(e) => setEditFormData({ ...editFormData, teacherTypeId: e.value })}
                                    placeholder="Select teacher type"
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </div>

                        {/* Action buttons */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                            <Button
                                label="Cancel"
                                icon="pi pi-times"
                                className="p-button-text"
                                onClick={() => setShowEditDialog(false)}
                            />
                            <Button
                                label={saving ? 'Saving...' : 'Save Changes'}
                                icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                                onClick={handleSaveEdit}
                                disabled={saving || !editFormData.firstName || !editFormData.lastName}
                                style={{ background: '#3498db', border: 'none' }}
                            />
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
}

export default EnrollmentSync;
